import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  DatePicker,
  Autocomplete,
  DataGrid,
  MenuCustom,
  Loading,
  IconButton,
  KpiCard,
} from "../../components";
import { ButtonMenu, MenuAddActions } from "./components";
import { formatDate, formatPrice, responseErros } from "../../utils";
import { value, InputErros, PermisoesHelper } from "../../helpers";
import {
  BancosCaixasResponsaveisService,
  BancosCaixasService,
  CaixaService,
  notification,
  PessoaService,
  SessionService,
  MeioPagamentoService,
} from "../../services";
import { caixaValidator } from "./middlewares";
import "./caixa-style.scss";

const CARDS_CAIXA = [
  {
    key: "saldoDiaAnterior",
    label: "Saldo do Dia Anterior",
    color: "#f57c00",
    icon: "ph-clock-counter-clockwise",
  },
  {
    key: "totalEntradas",
    label: "Total de Entradas",
    color: "#1976d2",
    icon: "ph-arrow-circle-down",
  },
  {
    key: "totalSaidas",
    label: "Total de Saídas",
    color: "#d32f2f",
    icon: "ph-arrow-circle-up",
  },
  {
    key: "saldoDia",
    label: "Saldo do Dia",
    color: "#388e3c",
    icon: "ph-currency-dollar",
  },
  {
    key: "saldoAcumulado",
    label: "Saldo Acumulado",
    color: "#00796b",
    icon: "ph-bank",
  },
];

const Caixa = () => {
  const permissoesHelper = new PermisoesHelper();
  permissoesHelper.validarPermisao("caixa-visualizar");
  const [listaCaixas, setListasCaixas] = useState([]);
  const [listaPessoas, setListaPessoas] = useState([]);
  const [listaMeiosPagamentos, setListaMeiosPagamentos] = useState([]);
  const [totaisCaixa, setTotaisCaixa] = useState({});
  const [configuracoes, setConfiguracoes] = useState({});
  const [formCaixa, setFormCaixa] = useState({
    data: new Date().toISOString(),
  });
  const [transferenciaCaixa, setTransferenciaCaixa] = useState({
    dataMovimentacao: new Date(),
  });
  const [caixaAberto, setCaixaAberto] = useState(null);
  const [empresasConsolidadas, setEmpresasConsolidadas] = useState([]);
  const [acessoCaixa, setAcessoCaixa] = useState(false);
  const [contaLista, setContaLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputErro, setInputErro] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const menuId = "primary-add-actions-menu";

  const bancosCaixasService = new BancosCaixasService();
  const pessoaService = new PessoaService();
  const caixaService = new CaixaService();
  const sessionService = new SessionService();
  const meioPagamentoService = new MeioPagamentoService();
  const bancosCaixasResponsaveisService = new BancosCaixasResponsaveisService();
  const dadosEmpresa = sessionService.getEmpresaAndUsuario();
  const inputErros = new InputErros(inputErro, setInputErro);
  const cardsCaixa = CARDS_CAIXA.filter(
    (card) =>
      card.key !== "saldoDiaAnterior" ||
      !configuracoes?.zerarSaldoAnteriorCaixa,
  );

  const caixaConsolidado = empresasConsolidadas.length > 1;
  const movimentacaoDeOutraEmpresa = (movimentacao) =>
    movimentacao.empresaId !== dadosEmpresa.empresa?.id;
  const nomeEmpresa = (empresaId) =>
    dadosEmpresa.usuario?.acessosEmpresas?.find(
      (empresa) => empresa.id === empresaId,
    )?.fantasia ?? `Empresa ${empresaId}`;

  const renderTitleCategoriaMovimentacao = (tipo) => {
    switch(tipo) {
      case "NOTA_CREDITO":
        return "Nota de Crédito";
      case "RETIRADA_BAIXA":
        return "Retirada de Baixa";
      case "NOTA_DEBITO":
        return "Nota de Débito";
      default:
        return "Título Normal";
    }
  }

  const columns = [
    {
      field: "categoriaMovimentacao",
      headerName: "Tipo",
      flex: 200,
      renderCell: (params) => {
        switch (params.value) {
          case "SUPRIMENTO":
            return tipoCategoriaMovimentacao("#4FBB53", "Suprimento");
          case "SANGRIA":
            return tipoCategoriaMovimentacao("#DC3545", "Sangria");
          case "TRANSFERENCIA":
            return tipoCategoriaMovimentacao(
              params.row?.tipo === "ENTRADA" ? "#4FBB53" : "#DC3545",
              "Transferência",
            );
          default:
            return tipoCategoriaMovimentacao(
              params.row?.modelo === "RECEITA" ? "#4FBB53" : "#DC3545",
              renderTitleCategoriaMovimentacao(params.row?.tipo)
            );
        }
      },
    },
    ...(caixaConsolidado
      ? [
          {
            field: "empresaId",
            headerName: "Empresa",
            flex: 250,
            hideSortIcons: true,
            sortable: false,
            valueGetter: (params) => nomeEmpresa(params.value),
          },
        ]
      : []),
    {
      field: "numeroTitulo",
      headerName: "Nº Documento",
      flex: 250,
      hideSortIcons: true,
      sortable: false,
    },
    {
      field: "clienteFornecedor",
      headerName: "Cliente/Fornecedor",
      flex: 300,
      hideSortIcons: true,
      sortable: false,
      valueGetter: (params) =>
        listaPessoas.find((item) => item.id == params.value)?.nomeRazaoSocial ??
        "",
    },
    {
      field: "valor",
      headerName: "Valor R$",
      flex: 250,
      hideSortIcons: true,
      sortable: false,
      valueGetter: (params) =>
        params.row.tipo === "NOTA_CREDITO" ||
        (params.row.tipo === "TITULO_NORMAL" && params.row.modelo === "DESPESA")
          ? `- ${formatPrice(params.value)}`
          : formatPrice(params.value),
    },
    {
      field: "fatMeioPagamentoId",
      headerName: "Meio Pagamento",
      flex: 250,
      hideSortIcons: true,
      sortable: false,
      valueGetter: (params) =>
        listaMeiosPagamentos.find((item) => item.id == params.value)
          ?.descricao ?? "",
    },
    {
      field: "saldo",
      headerName: "Saldo",
      flex: 250,
      hideSortIcons: true,
      sortable: false,
      valueGetter: (params) => formatPrice(params.value),
    },
    {
      field: "acoes",
      headerName: "Ações",
      sortable: false,
      disableClickEventBubbling: true,
      renderCell: (params) => {
        if (movimentacaoDeOutraEmpresa(params.row)) return;
        switch (params.row.categoriaMovimentacao) {
          case "SUPRIMENTO":
            return (
              permissoesHelper.temPermisao("suprimento-excluir") &&
              removerCategoriaMovimentacao(params.row)
            );
          case "SANGRIA":
            return (
              permissoesHelper.temPermisao("sangria-excluir") &&
              removerCategoriaMovimentacao(params.row)
            );
          case "TRANSFERENCIA":
            return (
              permissoesHelper.temPermisao("transferencia-saldo-excluir") &&
              removerCategoriaMovimentacao(params.row)
            );
          default:
            return;
        }
      },
    },
  ];

  function removerCategoriaMovimentacao(movimentacao) {
    return (
      <div className="d-flex align-items-center">
        <IconButton
          onClick={() => {
            removerTransferenciaSaldo(movimentacao);
          }}
        >
          <i
            className="ph-fill ph-trash"
            style={{ color: "#494C62", fontSize: 18 }}
          ></i>
        </IconButton>
      </div>
    );
  }

  function tipoCategoriaMovimentacao(color, text) {
    return (
      <>
        <span className="d-flex justify-content-center align-items-center">
          <span className="me-2">
            <i className="ph-fill ph-circle" style={{ color }}></i>
          </span>
          <span>{text}</span>
        </span>
      </>
    );
  }

  const handleAddActionsOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    buscarBancoCaixaResponsavel();
    buscarConfiguracao();
  }, []);

  const buscarBancoCaixaResponsavel = async () => {
    setLoading(true);
    const result = await bancosCaixasResponsaveisService.getAll({
      usuarioId: dadosEmpresa.usuario.id,
      nonPaginated: true,
    });
    if (!result.isAxiosError) {
      const arrayData = result.data.map((bancosIds) => bancosIds.bancoCaixaId);
      if (arrayData?.length) {
        await buscaContas(arrayData);
      } else {
        setAcessoCaixa(true);
        notification.alertaGenericos(
          "Você não tem acesso a nenhum caixa. Entre em contato com o administrador do sistema",
        );
      }
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscaContas = async (data) => {
    const filtro = {
      nonPaginated: true,
      id: data,
    };
    bancosCaixasService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setContaLista(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const validarStatusCaixa = (formCaixa) => {
    caixaService
      .caixaAberto(formCaixa.data, formCaixa.bancoCaixaId)
      .then((result) => {
        if (!result.isAxiosError) {
          setCaixaAberto(result.data);
        } else {
          responseErros(result);
        }
      });
  };

  const buscarConfiguracao = () => {
    if (dadosEmpresa.empresa?.configuracoes) {
      const data = dadosEmpresa.empresa.configuracoes;
      setConfiguracoes(data);
    }
  };

  const buscarCaixa = async (formCaixa, filter) => {
    setLoading(true);
    const result = await caixaService.getRelatorios(
      formCaixa.data,
      formCaixa.bancoCaixaId,
      {
        ...filter,
        orderBy: "dataPagamento",
        order: "ASC",
        zerarSaldoAnteriorCaixa: configuracoes?.zerarSaldoAnteriorCaixa,
      },
    );
    if (!result.isAxiosError) {
      validarStatusCaixa(formCaixa);
      setEmpresasConsolidadas(result.data.empresasIds ?? []);
      setListasCaixas(result.data.rows.reverse());
      setTotaisCaixa(result.data.totaisCaixa);
      if (result.data.rows.length) {
        buscarPessoas(result.data.rows);
        buscarMeiosPagamentos(result.data.rows);
      }
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarMeiosPagamentos = (listaCaixas) => {
    const listaIds = [
      ...new Set(listaCaixas.map((item) => item.fatMeioPagamentoId)),
    ];
    if (listaIds.length > 0) {
      const filtro = {
        id: listaIds,
        nonPaginated: true,
      };
      meioPagamentoService.getAll(filtro).then((result) => {
        if (!result.isAxiosError) {
          setListaMeiosPagamentos(result.data);
        } else {
          responseErros(result);
        }
      });
    }
  };

  const buscarPessoas = (listaCaixas) => {
    const listaIds = listaCaixas
      .filter((item) => item.clienteFornecedor)
      .map((item) => item.clienteFornecedor);
    if (listaIds.length > 0) {
      const filtro = {
        id: listaIds,
        nonPaginated: true,
        attributes: ["id", "nomeRazaoSocial"],
        disabledInclude: true,
      };
      pessoaService.getAll(filtro).then((result) => {
        if (!result.isAxiosError) {
          setListaPessoas(result.data);
        } else {
          responseErros(result);
        }
      });
    }
  };

  const onChangeDate = (name, date) => {
    if (!date) {
      date = "Invalid Date";
    }
    const formCaixaTemp = {
      ...formCaixa,
      [name]: date != "Invalid Date" ? formatDate.toSend(date) : "",
    };
    setFormCaixa(formCaixaTemp);
    if (formCaixaTemp.data && formCaixa.bancoCaixaId) {
      buscarCaixa(formCaixaTemp);
    }
  };

  const buscarConta = (name, value) => {
    const formCaixaTemp = {
      ...formCaixa,
      [name]: value ? value.id : undefined,
    };
    setFormCaixa(formCaixaTemp);
    if (formCaixa.data && formCaixaTemp.bancoCaixaId) {
      buscarCaixa(formCaixaTemp);
    }
  };

  const onClickCadastrarCaixa = async (aberto) => {
    caixaValidator
      .validate(formCaixa, { abortEarly: false })
      .then(() => {
        cadastrarCaixa({ ...formCaixa, aberto });
      })
      .catch((err) => {
        inputErros.set(err);
      });
  };

  const cadastrarCaixa = async (formCaixa) => {
    const result = await caixaService.cadastrar(formCaixa);
    if (!result.isAxiosError) {
      notification.alteracaoSucesso();
      buscarCaixa(formCaixa);
    } else {
      responseErros(result);
    }
  };

  const removerTransferenciaSaldo = async (movimentacao) => {
    const { categoriaMovimentacao, numeroTitulo } = movimentacao;
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      "Tem certeza que deseja excluir essa transação?",
    );
    if (retornoAlerta.isConfirmed) {
      setLoading(true);
      let res = {};
      switch (categoriaMovimentacao) {
        case "SUPRIMENTO":
          res = await caixaService.removerSuprimento(numeroTitulo);
          break;
        case "SANGRIA":
          res = await caixaService.removerSangria(numeroTitulo);
          break;
        case "TRANSFERENCIA":
          res = await caixaService.removerTransferenciaSaldo(numeroTitulo);
          break;
        default:
          break;
      }
      if (!res.isAxiosError) {
        await buscarCaixa(formCaixa);
      } else {
        setLoading(false);
        responseErros(res);
      }
    }
  };

  return (
    <div className="caixa-layout-container">
      <MenuCustom
        codigo={
          <IconButton
            aria-label="add action caixa"
            aria-controls={menuId}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? "true" : undefined}
            onClick={handleAddActionsOpen}
            color="inherit"
            style={{
              borderRadius: 5,
              backgroundColor: "#E5E5E5",
            }}
          >
            <i className="ph-fill ph-plus-square" style={{ fontSize: 23 }}></i>
          </IconButton>
        }
        ButtonMenu={
          <ButtonMenu
            onClickCadastrarCaixa={onClickCadastrarCaixa}
            caixaAberto={caixaAberto}
          />
        }
      />
      <Card className="caixa-layout-card">
        <CardHeader
          title="Caixa"
          action={
            <>
              {caixaAberto !== null && (
                <span
                  className={`badge ${
                    caixaAberto ? "bg-success" : "bg-danger"
                  } text-wrap p-2 me-3`}
                >
                  {caixaAberto ? "ABERTO" : "FECHADO"}
                </span>
              )}
            </>
          }
        />
        <CardContent className="caixa-layout-card-content">
          <Grid container spacing={3} className="mb-2" alignItems="center">
            <Grid item xs={3}>
              <DatePicker
                id="data"
                name="data"
                label="Data"
                format="dd/MM/yyyy"
                margin
                required
                error={inputErros.get("data")}
                value={value.date(formCaixa.data)}
                onChange={(date) => onChangeDate("data", date)}
              />
            </Grid>
            <Grid item xs={5}>
              <Autocomplete
                id="bancoCaixaId"
                name="bancoCaixaId"
                options={contaLista}
                noOptionsText="Sem opções"
                autoHighlight
                getOptionLabel={(option) => (option.nome ? option.nome : "")}
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Conta"
                    margin="normal"
                    required
                    variant="outlined"
                    error={inputErros.get("bancoCaixaId")}
                  />
                )}
                onChange={(e, value) => buscarConta("bancoCaixaId", value)}
                value={value.autoComplete(contaLista, formCaixa.bancoCaixaId)}
              />
            </Grid>
            {formCaixa.data && formCaixa.bancoCaixaId ? (
              <Grid item xs={2} sx={{ mt: "0.5em" }}>
                <IconButton
                  onClick={() => buscarCaixa(formCaixa)}
                  sx={{
                    backgroundColor: "#EDEEF0",
                    "&:hover": { backgroundColor: "#B6BABC" },
                  }}
                >
                  <i
                    className="ph-fill ph-arrows-clockwise"
                    style={{ fontSize: 22 }}
                  ></i>
                </IconButton>
              </Grid>
            ) : null}
          </Grid>
          <Grid container spacing={2} className="mt-1">
            {cardsCaixa.map((card) => (
              <Grid
                item
                xs={6}
                md={cardsCaixa.length === 5 ? 2.4 : 3}
                key={card.key}
              >
                <KpiCard
                  title={card.label}
                  value={formatPrice(totaisCaixa[card.key] ?? 0, false)}
                  color={card.color}
                  icon={card.icon}
                  loading={loading}
                />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={3} className="mt-4" style={{ flex: 1 }}>
            <Grid
              item
              xs={12}
              style={{ display: "flex", flexDirection: "column", flex: 1 }}
            >
              <DataGrid
                rows={listaCaixas}
                columns={columns}
                paginationMode="client"
                noFilter
                disableAcoes
                style={{ flex: 1 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <MenuAddActions
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        acessoCaixa={acessoCaixa}
        listaContasUsuario={contaLista}
        listaCaixas={listaCaixas}
        totaisCaixa={totaisCaixa}
        formCaixa={formCaixa}
        setFormCaixa={setFormCaixa}
        transferenciaCaixa={transferenciaCaixa}
        setTransferenciaCaixa={setTransferenciaCaixa}
        setTotaisCaixa={setTotaisCaixa}
        buscarCaixa={buscarCaixa}
        configuracoes={configuracoes}
        caixaAberto={caixaAberto}
      />
      <Loading loading={loading} />
    </div>
  );
};

export default Caixa;
